/**
 * web/src/pages/Settings.tsx
 *
 * Phase 6.5 UI-14 — Settings page mounted at /settings. Single-column
 * layout (max-w-[720px]) — no left rail because V1 has exactly one card.
 *
 * Page responsibilities:
 *   1. Publish topbar title via useSetTopbar (no breadcrumbs / no actions).
 *   2. Consume ?gmail=... querystring on mount, fire one toast, then clear
 *      the querystring via setSearchParams({}, { replace: true }) so a
 *      page-refresh doesn't re-toast. (UI-SPEC § Querystring consumption.)
 *   3. Render <GmailConnectionCard /> — the only V1 card.
 *   4. Render an <Empty /> fallback for non-401 useMe() errors. (401 is
 *      handled by the global queryClient redirect to /login.)
 *
 * Future settings cards mount as SIBLINGS of GmailConnectionCard below
 * (not as a route or sub-nav) — UI-SPEC § Settings Page Layout.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { qk } from '../lib/queryKeys';
import { useMe } from '../hooks/queries/useMe';
import { useSetTopbar } from '../components/layout/Topbar';
import { Empty } from '../components/shared/Empty';
import { Icon } from '../lib/icons';
import { GmailConnectionCard } from '../components/settings/GmailConnectionCard';
import { PurgeWorkflowsCard } from '../components/settings/PurgeWorkflowsCard';
import { ApiClientError } from '../lib/api';

/**
 * Maps the `?gmail=error&reason=...` reason values to user-facing toast copy.
 * Unknown reasons fall through to the generic "other" message — matches
 * UI-SPEC § Error state copy.
 */
function errorMessageForReason(reason: string | null): string {
  if (reason === 'auth') {
    return 'Google rejected the request. Please try connecting again.';
  }
  if (reason === 'denied') {
    return "Connection cancelled. You can try again when you're ready.";
  }
  return "Couldn't connect Gmail. Reach out in #pixel-tooling for help.";
}

/**
 * Renders the Settings page.
 *
 * @sideEffects subscribes to useMe; reads useSearchParams; fires sonner
 *              toasts + invalidates qk.me() on mount when ?gmail=connected.
 * @idempotent yes — toast effect is mount-only and clears the querystring.
 */
export function Settings() {
  const [search, setSearch] = useSearchParams();
  const queryClient = useQueryClient();
  const { error } = useMe();

  useSetTopbar({ title: 'Settings' });

  // Mount-only effect: read ?gmail=..., fire one toast, clear querystring.
  useEffect(() => {
    const gmailParam = search.get('gmail');
    if (!gmailParam) return;

    if (gmailParam === 'connected') {
      toast.success('Gmail connected. Workflow emails will now send from your address.');
      queryClient.invalidateQueries({ queryKey: qk.me() });
    } else if (gmailParam === 'error') {
      toast.error(errorMessageForReason(search.get('reason')));
    }

    // Strip the querystring so refresh doesn't re-fire the toast.
    setSearch({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Non-401 error fallback. 401 is handled by the global queryClient redirect.
  const isAuthError = error instanceof ApiClientError && error.status === 401;
  if (error && !isAuthError) {
    return (
      <div className="max-w-[720px]">
        <Empty
          icon={<Icon name="alert" size={20} />}
          title="Couldn't load your settings"
          description={error.message || 'Try refreshing the page.'}
        />
      </div>
    );
  }

  return (
    <div className="max-w-[720px]">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.08em] font-semibold text-text-faint mb-1.5">
          Account
        </div>
        <h1 className="text-[24px] font-semibold -tracking-[0.015em] text-text leading-tight">
          Settings
        </h1>
        <p className="text-[12.5px] text-text-subtle mt-1.5">
          Manage how Pixel sends emails on your behalf.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <GmailConnectionCard />
        {/* Admin + non-production only; self-gates to null otherwise. */}
        <PurgeWorkflowsCard />
        {/* Future settings cards mount here as siblings. */}
      </div>
    </div>
  );
}
